import { Link } from "react-router-dom";
import BackgroundLayer, {
  type BackgroundVariant,
} from "../BackgroundLayer";
import StatusBar from "../StatusBar";
import CaseHeader from "./CaseHeader";
import CommandBar, { type CommandKey } from "./CommandBar";
import { useCaseOrPreview } from "../../hooks/useCaseOrPreview";
import { useLatestParseJobForCase } from "../../hooks/useParseJob";

interface Props {
  caseId: string | undefined;
  variant: BackgroundVariant;
  /** Which command is the active one (highlighted in the command bar). */
  currentCommand?: CommandKey;
  children: React.ReactNode;
}

/**
 * Shared chrome for every per-case surface: War Room and the five command
 * pages. Handles case loading, preview mode, and 404. Renders status bar,
 * background, case header, page content, and command bar.
 */
function CommandLayout({ caseId, variant, currentCommand, children }: Props) {
  const { caseRow, isLoading, error, isPreview } = useCaseOrPreview(caseId);
  const { data: parseJob } = useLatestParseJobForCase(caseRow?.id);

  return (
    <div className="relative min-h-screen text-bone">
      <BackgroundLayer variant={variant} overlay="heavy" />
      <StatusBar />

      <main className="relative z-10 mx-auto flex max-w-7xl flex-col gap-4 px-6 pb-10 pt-6">
        {isLoading ? (
          <LoadingShell />
        ) : error ? (
          <ErrorShell message={error.message} />
        ) : !caseRow ? (
          <NotFoundShell />
        ) : (
          <>
            {isPreview ? <PreviewBanner /> : null}
            {parseJob?.status === "extracting_paragraphs" ? (
              <ExtractionBanner />
            ) : null}
            {parseJob?.status === "failed" ? (
              <ExtractionFailedBanner message={parseJob.error_message ?? ""} />
            ) : null}
            <CommandBar caseId={caseRow.id} currentCommand={currentCommand} />
            <CaseHeader caseRow={caseRow} />
            {children}
          </>
        )}
      </main>

      {/* Floating war-room music player. Renders on every per-case
          surface so the soundtrack persists as the operator moves
          between commands. Audio element lives inside the widget,
          so playback continues across React re-renders within a page. */}
    </div>
  );
}

function LoadingShell() {
  return (
    <div className="mt-20 flex items-center justify-center">
      <p className="flex items-center gap-3 font-display text-base font-light uppercase tracking-[0.16em] text-bone-mute">
        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow" />
        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow [animation-delay:200ms]" />
        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow [animation-delay:400ms]" />
        opening the war room
      </p>
    </div>
  );
}

function ErrorShell({ message }: { message: string }) {
  return (
    <FrameMessage
      label="the lines are cut"
      title="The case did not load."
      body={message}
      borderClass="border-ember-bright/40 bg-ember/20"
      titleClass="text-ember-bright"
    />
  );
}

function NotFoundShell() {
  return (
    <FrameMessage
      label="no such matter"
      title="This case is not visible to you."
      body="Either it does not exist, or you lack the case role required to see it."
    />
  );
}

function FrameMessage({
  label,
  title,
  body,
  borderClass = "border-steel-dark bg-iron/70",
  titleClass = "text-bone",
}: {
  label: string;
  title: string;
  body: string;
  borderClass?: string;
  titleClass?: string;
}) {
  return (
    <div
      className={`mx-auto mt-12 max-w-2xl rounded border px-6 py-8 backdrop-blur-sm ${borderClass}`}
    >
      <p className="font-display text-base font-light uppercase tracking-[0.16em] text-bone-mute">
        {label}
      </p>
      <p className={`mt-2 font-display text-2xl font-light ${titleClass}`}>
        {title}
      </p>
      <p className="mt-3 font-display text-sm text-bone-dim">{body}</p>
      <Link
        to="/"
        className="mt-5 inline-block font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute hover:text-bone"
      >
        ← campaigns
      </Link>
    </div>
  );
}

function PreviewBanner() {
  return (
    <div className="rounded border border-ember-bright/40 bg-ember/20 px-4 py-2 backdrop-blur-sm">
      <p className="font-display text-base font-light uppercase tracking-[0.14em] text-ember-bright">
        ⚠ preview mode // mock data — no backend wired yet
      </p>
    </div>
  );
}

function ExtractionBanner() {
  return (
    <div className="rounded border border-blue-bright/40 bg-iron-2/60 px-4 py-2.5 backdrop-blur-sm">
      <p className="flex items-center gap-3 font-display text-base font-light uppercase tracking-[0.14em] text-blue-bright">
        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-blue-bright shadow-ember-soft" />
        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-blue-bright shadow-ember-soft [animation-delay:200ms]" />
        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-blue-bright shadow-ember-soft [animation-delay:400ms]" />
        // paragraphs extracting in background — typically 60-150s. retriever search lights up as they land.
      </p>
    </div>
  );
}

function ExtractionFailedBanner({ message }: { message: string }) {
  return (
    <div className="rounded border border-ember-bright/50 bg-ember/15 px-4 py-2.5 backdrop-blur-sm">
      <p className="font-display text-base font-light uppercase tracking-[0.14em] text-ember-bright">
        // paragraph extraction failed
      </p>
      <p className="mt-1 font-display text-base text-bone-dim">{message}</p>
    </div>
  );
}

export default CommandLayout;
